/**
 * Shared Document Management Drawer
 *
 * A reusable drawer component for both Asset Lens and System Lens
 * Features drag & drop, card-based layout, inline actions, and smart organization.
 */

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Input, message, Upload } from 'antd'
import { RcFile } from 'antd/es/upload'
import { useGeckoAnalytics } from 'features/analytics'
import { DocumentData } from 'features/baseLens'
import {
  File,
  FileText,
  Search,
  Upload as UploadIconLucide,
  X,
} from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import styled, { keyframes } from 'styled-components'

import { DocumentCard } from './DocumentCard'
import { DocumentModal } from './DocumentModal'

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

// Backdrop overlay
const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(0.25rem);
  z-index: 999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

// Main panel container
const PanelContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 25rem;
  height: 100vh;
  background: ${({ theme }) => theme.forecast.background1};
  border-left: 1px solid ${({ theme }) => theme.forecast.background3};
  box-shadow: -0.5rem 0 2rem rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.03) 0%,
      rgba(255, 255, 255, 0.02) 50%,
      rgba(0, 0, 0, 0.1) 100%
    );
    pointer-events: none;
  }
`

// Panel header
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: ${({ theme }) => theme.forecast.background2};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.forecast.primary} 0%,
      ${({ theme }) => theme.forecast.primary} 100%
    );
  }
`

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  background: ${({ theme }) => theme.forecast.background3};
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  position: relative;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${({ theme }) => theme.forecast.activeText};
    position: relative;
    z-index: 1;
  }
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

const PanelTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.125rem;
`

const PanelSubtitle = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  align-items: center;
  gap: 0.375rem;

  &::before {
    content: '';
    width: 0.375rem;
    height: 0.375rem;
    background: ${({ theme }) => theme.forecast.primary};
    border-radius: 50%;
  }
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  border-radius: 0.5rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.defaultText};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ theme }) => theme.forecast.background2};
    border-color: ${({ theme }) => theme.forecast.defaultText};
    color: ${({ theme }) => theme.forecast.activeText};
    transform: translateY(-1px);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`

// Upload zone
const UploadZone = styled.div<{ $isDragOver: boolean; $isUploading: boolean }>`
  padding: 2rem 1.5rem;
  border: 2px dashed
    ${({ theme, $isDragOver }) =>
      $isDragOver ? theme.forecast.primary : theme.forecast.background3};
  border-radius: 0.75rem;
  background: ${({ theme, $isDragOver }) =>
    $isDragOver ? `${theme.forecast.primary}10` : theme.forecast.background2};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${({ $isUploading }) => ($isUploading ? 0.6 : 1)};
  pointer-events: ${({ $isUploading }) => ($isUploading ? 'none' : 'auto')};

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    background: ${({ theme }) => `${theme.forecast.primary}05`};
  }
`

const StyledUploadIcon = styled(UploadIconLucide)`
  width: 2rem;
  height: 2rem;
  color: ${({ theme }) => theme.forecast.primary};
  margin-bottom: 0.75rem;
`

const UploadText = styled.p`
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 1rem;
  font-weight: 500;
  margin: 0 0 0.5rem;
`

const UploadHint = styled.p`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  margin: 0;
`

// Upload progress
const UploadProgress = styled.div`
  padding: 0.75rem;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const ProgressSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid ${({ theme }) => theme.forecast.background3};
  border-top: 2px solid ${({ theme }) => theme.forecast.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const ProgressText = styled.span`
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  font-weight: 500;
  flex: 1;
`

const SearchInput = styled(Input)`
  && {
    background: ${({ theme }) => theme.forecast.background2};
    border: 1px solid ${({ theme }) => theme.forecast.background3};
    border-radius: 0.5rem;
    color: ${({ theme }) => theme.forecast.activeText};
    font-size: 0.875rem;
    padding: 0.75rem;

    &:focus {
      border-color: ${({ theme }) => theme.forecast.primary};
      box-shadow: 0 0 0 2px ${({ theme }) => `${theme.forecast.primary}10`};
    }

    &::placeholder {
      color: ${({ theme }) => theme.forecast.secondaryText};
    }

    .ant-input {
      background: transparent;
      border: none;
      color: ${({ theme }) => theme.forecast.activeText};
      padding: 0;

      &:focus {
        box-shadow: none;
      }
    }
  }
`

// Content area
const PanelContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ContentArea = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.forecast.background2};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.forecast.background3};
    border-radius: 0.1875rem;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.forecast.secondaryText};
  }
`

// Documents list
const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

// Empty state
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const EmptyIcon = styled(FileText)`
  width: 4rem;
  height: 4rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-bottom: 1rem;
`

const EmptyTitle = styled.h3`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
`

const EmptyDescription = styled.p`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  margin: 0;
`

interface FilesDrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  documents: DocumentData[]
  onUpload: (
    file: File,
    metadata: { title: string; description: string },
    onStatusUpdate?: (status: string) => void
  ) => Promise<{ success: boolean; document?: DocumentData; message?: string }>
  onDelete: (
    documentId: string
  ) => Promise<{ success: boolean; message?: string }>
  onRefresh?: () => void
  emptyStateTitle?: string
  emptyStateDescription?: string
  // Optional tracking context
  page?: string
  entityId?: string
  entityName?: string
  entityType?: 'asset' | 'system'
}

export const FilesDrawer: React.FC<FilesDrawerProps> = ({
  isOpen,
  onClose,
  title = 'Documents',
  subtitle,
  documents,
  onUpload,
  onDelete,
  onRefresh,
  emptyStateTitle = 'No documents yet',
  emptyStateDescription = 'Upload your first document to get started. Drag & drop or click the upload zone above.',
  page,
  entityId,
  entityName,
  entityType,
}) => {
  const { trackEvent } = useGeckoAnalytics()
  const [searchText, setSearchText] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState('')
  const [viewModal, setViewModal] = useState<DocumentData | undefined>()

  const trackingContext = useMemo(
    () => ({
      ...(page && { page }),
      ...(entityId && { entity_id: entityId }),
      ...(entityName && { entity_name: entityName }),
      ...(entityType && { entity_type: entityType }),
    }),
    [page, entityId, entityName, entityType]
  )

  // Filter documents based on search
  const filteredDocuments = useMemo(() => {
    if (!searchText) return documents

    const searchLower = searchText.toLowerCase()
    return documents.filter(
      (doc) =>
        doc.title.toLowerCase().includes(searchLower) ||
        doc.description.toLowerCase().includes(searchLower) ||
        doc.type.toLowerCase().includes(searchLower)
    )
  }, [documents, searchText])

  /**
   * Handle file upload via drag & drop or click
   */
  const handleFileUpload = useCallback(
    async (file: RcFile): Promise<boolean> => {
      try {
        setIsUploading(true)

        // Track document upload attempt
        trackEvent('Document Upload Started', {
          filename: file.name,
          file_size: file.size,
          file_type: file.type,
          ...trackingContext,
        })

        const result = await onUpload(
          file,
          {
            title: file.name,
            description: '',
          },
          setUploadStatus
        )

        if (result.success) {
          message.success('Document uploaded successfully')
          onRefresh?.()

          // Track successful upload
          trackEvent('Document Uploaded', {
            filename: file.name,
            file_size: file.size,
            file_type: file.type,
            document_id: result.document?.id,
            ...trackingContext,
          })
        } else {
          message.error(result.message || 'Failed to upload document')

          // Track upload failure
          trackEvent('Document Upload Failed', {
            filename: file.name,
            file_size: file.size,
            file_type: file.type,
            error_message: result.message,
            ...trackingContext,
          })
        }

        return result.success
      } catch (error) {
        console.error('Upload failed:', error)
        message.error('Failed to upload document')

        // Track upload error
        trackEvent('Document Upload Error', {
          filename: file.name,
          file_size: file.size,
          file_type: file.type,
          error: error instanceof Error ? error.message : 'Unknown error',
          ...trackingContext,
        })

        return false
      } finally {
        setIsUploading(false)
        setUploadStatus('')
      }
    },
    [onUpload, onRefresh, trackEvent, trackingContext]
  )

  /**
   * Handle document deletion
   */
  const handleDeleteDocument = useCallback(
    async (document: DocumentData) => {
      try {
        // Track document deletion attempt
        trackEvent('Document Delete Started', {
          document_id: document.id,
          document_title: document.title,
          document_type: document.type,
          ...trackingContext,
        })

        const result = await onDelete(document.id)
        if (result.success) {
          message.success('Document deleted successfully')
          onRefresh?.()

          // Track successful deletion
          trackEvent('Document Deleted', {
            document_id: document.id,
            document_title: document.title,
            document_type: document.type,
            ...trackingContext,
          })
        } else {
          message.error(result.message || 'Failed to delete document')

          // Track deletion failure
          trackEvent('Document Delete Failed', {
            document_id: document.id,
            document_title: document.title,
            document_type: document.type,
            error_message: result.message,
            ...trackingContext,
          })
        }
      } catch (error) {
        console.error('Delete failed:', error)
        message.error('Failed to delete document')
      }
    },
    [onDelete, onRefresh, trackEvent, trackingContext]
  )

  /**
   * Handle drag events
   * Note: We only handle visual feedback here. The actual file processing
   * is handled by the Ant Design Upload component to avoid duplicate uploads.
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    // Don't handle the file here - let the Upload component handle it
  }, [])

  // Use React Portal to render at document body level
  return ReactDOM.createPortal(
    <>
      <Backdrop $isOpen={isOpen} onClick={onClose} />
      <PanelContainer $isOpen={isOpen}>
        <PanelHeader>
          <HeaderInfo>
            <HeaderIcon>
              <FileText />
            </HeaderIcon>
            <HeaderText>
              <PanelTitle>{title}</PanelTitle>
              <PanelSubtitle>
                {subtitle || `${documents.length} files available`}
              </PanelSubtitle>
            </HeaderText>
          </HeaderInfo>
          <CloseButton onClick={onClose} aria-label='Close documents panel'>
            <X size={20} />
          </CloseButton>
        </PanelHeader>

        <PanelContent>
          <ContentArea>
            {/* Upload Zone */}
            <Upload
              accept='.pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg'
              showUploadList={false}
              beforeUpload={handleFileUpload}
              disabled={isUploading}>
              <UploadZone
                $isDragOver={dragOver}
                $isUploading={isUploading}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}>
                <StyledUploadIcon />
                <UploadText>
                  {isUploading
                    ? 'Uploading...'
                    : 'Drop files here or click to upload'}
                </UploadText>
                <UploadHint>
                  Support for PDF, Word, Excel, and image documents
                </UploadHint>
              </UploadZone>
            </Upload>

            {/* Upload Progress */}
            {isUploading && uploadStatus && (
              <UploadProgress>
                <ProgressSpinner />
                <ProgressText>{uploadStatus}</ProgressText>
              </UploadProgress>
            )}

            {/* Search */}
            <SearchInput
              placeholder='Search documents...'
              prefix={<Search size={16} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />

            {/* Documents List */}
            <DocumentsList>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    onView={() => {
                      setViewModal(doc)
                      // Track document view
                      trackEvent('Document Viewed', {
                        document_id: doc.id,
                        document_title: doc.title,
                        document_type: doc.type,
                        ...trackingContext,
                      })
                    }}
                    onDownload={() => {
                      if (doc.signedUrl) {
                        // Track document download
                        trackEvent('Document Downloaded', {
                          document_id: doc.id,
                          document_title: doc.title,
                          document_type: doc.type,
                          ...trackingContext,
                        })
                        const a = document.createElement('a')
                        a.href = doc.signedUrl
                        a.download = doc.filename || doc.title
                        document.body.appendChild(a)
                        a.click()
                        document.body.removeChild(a)
                      }
                    }}
                    onDelete={() => handleDeleteDocument(doc)}
                    canView={Boolean(doc.signedUrl)}
                    canDownload={Boolean(doc.signedUrl)}
                  />
                ))
              ) : searchText ? (
                <EmptyState>
                  <EmptyIcon />
                  <EmptyTitle>No documents found</EmptyTitle>
                  <EmptyDescription>
                    Try adjusting your search terms or upload a new document.
                  </EmptyDescription>
                </EmptyState>
              ) : (
                <EmptyState>
                  <EmptyIcon />
                  <EmptyTitle>{emptyStateTitle}</EmptyTitle>
                  <EmptyDescription>{emptyStateDescription}</EmptyDescription>
                </EmptyState>
              )}
            </DocumentsList>
          </ContentArea>
        </PanelContent>
      </PanelContainer>

      {/* View Document Modal */}
      <DocumentModal
        title={viewModal?.title}
        open={Boolean(viewModal)}
        onClose={() => setViewModal(undefined)}
        document={
          viewModal
            ? {
                signedUrl: viewModal.signedUrl,
                type: viewModal.type,
                filename: viewModal.filename,
                title: viewModal.title,
              }
            : undefined
        }
        width={900}
      />
    </>,
    document.body
  )
}

export default FilesDrawer
